// @ts-check
// Configurações > Privacidade > Base legal das comunicações de marketing (2.0.0, contrato 09 de campanhas base, seção
// 6.6): "Quem entrou no funil consentiu" (desligado, só o contato principal ou também os outros responsáveis adultos),
// a contagem de quem está com LGPD não consentida com o link para a lista, e as etapas "em atendimento" em que os
// comunicados de serviço não pedem autorização de marketing (K37; Matriculado sempre incluída). Só a flag SECURITY grava.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { SettingsSection } from '../settings/settings-pages.js';
import { getViewsForObject, objectsByNameSingular } from '../../data/metadata-store.js';
import { fetchMarketingConsentSettings, updateMarketingConsentSettings } from '../../data/marketing-consent.js';
import { serviceStagesToSave } from '../../lib/marketing-service-stages.js';
import { Link } from '../../app/router.js';
import { translateMetadataLabel, t } from '../../i18n/index.js';
import { Button } from '../../ui/button.js';
import { Checkbox, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { DEFAULT_MARKETING_FUNNEL_CONSENT_MODE } from '../../../../shared/privacy/marketing-consent-rules.js';

const FUNNEL_MODES = Object.freeze(['CONTACT', 'FAMILY_GUARDIANS']);
const ALWAYS_SERVICE_STAGE = 'ENROLLED';
// fora da relação ativa por definição: não aparecem na lista
const EXCLUDED_SERVICE_STAGES = new Set(['LOST', 'NOT_A_LEAD']);

/** @typedef {import('../../data/marketing-consent.js').MarketingConsentSettings} MarketingConsentSettings */

/** Lista "LGPD não consentida" (view do pacote filtrada pela marca), ou a lista de pessoas. */
function optedOutListHref() {
  const personObject = objectsByNameSingular.value.get('person');
  if (!personObject) return null;
  const markField = personObject.fields.find((field) => field.name === 'marketingOptOut');
  const view = markField
    ? getViewsForObject(personObject.id).find((candidate) => (candidate.viewFilters ?? []).some((filter) => filter.fieldMetadataId === markField.id))
    : undefined;
  return view ? `/objects/${personObject.namePlural}?viewId=${view.id}` : `/objects/${personObject.namePlural}`;
}

/** Etapas do funil que podem ficar em atendimento, na ordem do funil. */
function serviceStageOptions() {
  const stageField = objectsByNameSingular.value.get('enrollmentOpportunity')?.fields.find((field) => field.name === 'stage');
  return (stageField?.options ?? []).filter((option) => !EXCLUDED_SERVICE_STAGES.has(option.value));
}

/** @param {string[]} first @param {string[]} second */
const sameStages = (first, second) => first.length === second.length && first.every((stage) => second.includes(stage));

export function MarketingLegalBasisSection() {
  const [saved, setSaved] = useState(/** @type {MarketingConsentSettings | null} */ (null));
  const [hasFailed, setHasFailed] = useState(false);
  const [isFunnelOn, setIsFunnelOn] = useState(true);
  const [mode, setMode] = useState(/** @type {string} */ (DEFAULT_MARKETING_FUNNEL_CONSENT_MODE));
  const [stages, setStages] = useState(/** @type {string[]} */ ([]));
  const [isSaving, setIsSaving] = useState(false);

  /** @param {MarketingConsentSettings} settings */
  const applySaved = (settings) => {
    setSaved(settings);
    setIsFunnelOn(settings.funnelEntryConsent !== 'OFF');
    if (settings.funnelEntryConsent !== 'OFF') setMode(settings.funnelEntryConsent);
    setStages(settings.serviceRelationshipStages);
  };

  useEffect(() => {
    fetchMarketingConsentSettings()
      .then(applySaved)
      .catch((error) => {
        setHasFailed(true);
        showErrorSnackbar(error);
      });
  }, []);

  const nextMode = isFunnelOn ? mode : 'OFF';
  const isDirty = Boolean(saved) && (nextMode !== saved?.funnelEntryConsent || !sameStages(stages, saved?.serviceRelationshipStages ?? []));
  const isDisabled = !saved || hasFailed || !saved.canUpdate || isSaving;
  const stageOptions = serviceStageOptions();

  const save = async () => {
    if (!saved || !isDirty) return;
    setIsSaving(true);
    try {
      /** @type {{ funnelEntryConsent?: string, serviceRelationshipStages?: string[] }} */
      const patch = {};
      if (nextMode !== saved.funnelEntryConsent) patch.funnelEntryConsent = nextMode;
      if (!sameStages(stages, saved.serviceRelationshipStages)) patch.serviceRelationshipStages = serviceStagesToSave(stages, stageOptions);
      applySaved(await updateMarketingConsentSettings(patch));
      showSuccessSnackbar(t('marketingConsent.settings.saved'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStage = (/** @type {string} */ stage, /** @type {boolean} */ isChecked) => {
    setStages((current) => (isChecked ? [...new Set([...current, stage])] : current.filter((value) => value !== stage)));
  };
  const listHref = optedOutListHref();

  return html`<${SettingsSection} title=${t('marketingConsent.settings.title')} description=${t('marketingConsent.settings.description')}>
    <div class="marketing-basis" data-testid="marketing-legal-basis" data-loading=${saved ? undefined : 'true'}>
      <div class="marketing-basis-toggle">
        <${Toggle} value=${isFunnelOn} disabled=${isDisabled} ariaLabel=${t('marketingConsent.settings.funnelToggle')} onChange=${setIsFunnelOn} />
        <div class="marketing-basis-toggle-text">
          <span class="marketing-basis-toggle-title">${t('marketingConsent.settings.funnelToggle')}</span>
          <span class="channel-card-description">${t('marketingConsent.settings.funnelHint')}</span>
        </div>
      </div>
      ${isFunnelOn
        ? html`<fieldset class="marketing-basis-modes" disabled=${isDisabled}>
            <legend class="form-field-label">${t('marketingConsent.settings.modesLabel')}</legend>
            ${FUNNEL_MODES.map((option) => html`<label key=${option} class="marketing-basis-mode">
              <input type="radio" name="marketing-funnel-mode" value=${option} checked=${mode === option}
                onChange=${() => setMode(option)} />
              <span>${t(`marketingConsent.settings.modes.${option}`)}</span>
            </label>`)}
          </fieldset>`
        : null}
      <p class="marketing-basis-note"><${Icon} name="IconInfoCircle" size=${14} /><span>${t('marketingConsent.settings.minorNote')}</span></p>
      ${saved && saved.optedOutCount !== null
        ? html`<p class="marketing-basis-count" data-testid="marketing-opted-out-count">
            <${Icon} name="IconForbid" size=${14} />
            <span>${t('marketingConsent.settings.optedOutCount', { count: saved.optedOutCount })}</span>
            ${listHref ? html`<${Link} href=${listHref} className="operations-link">${t('marketingConsent.settings.openList')}<//>` : null}
          </p>`
        : null}
      ${stageOptions.length > 0
        ? html`<div class="marketing-basis-stages">
            <span class="form-field-label">${t('marketingConsent.settings.serviceStagesTitle')}</span>
            <span class="channel-card-description">${t('marketingConsent.settings.serviceStagesHint')}</span>
            <div class="marketing-basis-stage-list">
              ${stageOptions.map((option) => {
                const isLocked = option.value === ALWAYS_SERVICE_STAGE;
                const label = translateMetadataLabel(option.label);
                return html`<div key=${option.value} class="marketing-basis-stage" data-locked=${isLocked ? 'true' : undefined}
                  title=${isLocked ? t('marketingConsent.settings.serviceStageLocked') : label}>
                  <${Checkbox} checked=${isLocked || stages.includes(option.value)} disabled=${isDisabled || isLocked} ariaLabel=${label}
                    onChange=${(/** @type {boolean} */ isChecked) => toggleStage(option.value, isChecked)} />
                  <span>${label}</span>
                </div>`;
              })}
            </div>
          </div>`
        : null}
      ${saved && !saved.canUpdate ? html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('marketingConsent.settings.readOnly')}</p>` : null}
      <div class="marketing-basis-actions">
        <${Button} title=${t('marketingConsent.settings.save')} size="small" variant="secondary" accent="blue" isLoading=${isSaving}
          disabled=${isDisabled || !isDirty} onClick=${save} data-testid="marketing-legal-basis-save" />
      </div>
    </div>
  <//>`;
}
